/**
 * AdminGuard wraps all /admin/* pages.
 *
 * - While session is loading: shows a spinner (prevents flash of wrong content)
 * - If not admin: shows an access-denied screen (hard gate)
 * - If admin: renders children
 *
 * The session cookie is already validated server-side by Caddy → /auth/validate.
 * This guard is a UX safeguard, not the security boundary.
 */
import { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { useSession } from '../context/SessionContext'
import { Button, Spinner } from './ui'

export default function AdminGuard({ children }: { children: ReactNode }) {
  const { user, loading, error } = useSession()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner />
          Loading session…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          Session error: {error}
        </div>
      </div>
    )
  }

  if (!user?.isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-surface-muted text-muted">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">Access Denied</h1>
          <p className="mt-2 text-sm text-muted">
            Admin access requires the <code className="text-foreground">idefy-admins</code> group.
          </p>
          <a href="/catalog" className="mt-6 inline-block">
            <Button variant="outline" size="sm">Return to portal</Button>
          </a>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
